import { AfterViewInit, Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { DisplayModelDto } from '../../../models/display/display-model.dto';
import { DisplayTableDto } from '../../../models/display/display-table.dto';
import { DisplayTableTitleDto } from '../../../models/display/display-table-title.dto';
import { DisplayAttributeDto } from '../../../models/display/display-attribute.dto';
import { DisplayLinkDto } from '../../../models/display/display-link.dto';
import { DisplayCoordinateDto } from '../../../../../core/model/coordinates/display-coordinate.dto';
import { DisplayLinkActionDto } from '../../../models/display/display-link-action.dto';
import { DISPLAY_ORIGIN, FIRST_INDEX, HEAVY_FONT, LIGHT_FONT, NULL_LENGTH } from '../../../models/constant/constants.constant';

@Component({
  selector: 'app-model-canvas',
  standalone: true,
  imports: [],
  template: `<canvas #canvas class="diagram-background"></canvas>`,
  styleUrl: './model-canvas.component.scss',
})
export class ModelCanvasComponent implements AfterViewInit, OnDestroy {
  @ViewChild('canvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;
  private _ctx!: CanvasRenderingContext2D;
  private _canvas!: HTMLCanvasElement;
  @Input()
  model!: DisplayModelDto;

  ngAfterViewInit(): void {
    this._canvas = this.canvasRef.nativeElement;
    this._ctx = this._canvas.getContext('2d')!;

    this.resizeCanvas();
    window.addEventListener('resize', () => this.resizeCanvas());
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', () => this.resizeCanvas());
  }

  resizeCanvas(): void {
    const canvas = this.canvasRef.nativeElement;
    canvas.width = this.findMaxWidthAndHeight().x;
    canvas.height = this.findMaxWidthAndHeight().y;
    this.draw(this.model);
  }

  findMaxWidthAndHeight(): DisplayCoordinateDto {
    let maxX = DISPLAY_ORIGIN.x;
    let maxY = DISPLAY_ORIGIN.y;

    for (const table of this.model.tables) {
      const actualX = table.x + table.width;
      const actualY = table.y + table.height;
      maxX = Math.max(maxX, actualX);
      maxY = Math.max(maxY, actualY);
    }
    return { x: maxX, y: maxY };
  }

  draw(model: DisplayModelDto): void {
    this._ctx.clearRect(DISPLAY_ORIGIN.x, DISPLAY_ORIGIN.y, this._canvas.width, this._canvas.height);
    for (const table of model.tables) {
      this.drawTable(table);
    }
    for (const link of model.links) {
      this.drawLink(link);
    }
  }

  drawTable(table: DisplayTableDto): void {
    this._ctx.strokeRect(table.x, table.y, table.width, table.height);
    this.drawTableTitle(table.title);
    for (const attribute of table.attributes) {
      this.drawAttribute(attribute);
    }
  }

  drawTableTitle(title: DisplayTableTitleDto): void {
    this.setHeavyFont();
    this._ctx.fillText(title.name, title.x, title.y);
  }

  drawAttribute(attribute: DisplayAttributeDto): void {
    this.setLigthFont();
    this._ctx.fillText(attribute.name, attribute.x, attribute.y);
  }

  drawLink(link: DisplayLinkDto): void {
    if (link.points.length > NULL_LENGTH) {
      this._ctx.beginPath();
      this.moveTo(link.points[FIRST_INDEX]);
      link.points.forEach(link => this.lineTo(link));
      this._ctx.stroke();
    }
    this.drawLinkAction(link.action);
  }

  moveTo(point: DisplayCoordinateDto): void {
    this._ctx.moveTo(point.x, point.y);
  }

  lineTo(point: DisplayCoordinateDto): void {
    this._ctx.lineTo(point.x, point.y);
  }

  drawLinkAction(action: DisplayLinkActionDto): void {
    this.setLigthFont();
    this._ctx.fillText(action.label, action.point.x, action.point.y);
  }

  setLigthFont(): void {
    this._ctx.font = LIGHT_FONT;
  }

  setHeavyFont(): void {
    this._ctx.font = HEAVY_FONT;
  }
}
